// What? Works out where a newly added section should be spliced into the flat
//       sections array.
// Why? Sections are stored flat but represent a tree, and the backend hands them
//      back as a depth-first flattening on every fetch. Inserting a sibling in
//      the "wrong" slot (e.g. between a section and its children) therefore looks
//      right until the next reload, when the section jumps elsewhere.
// How? The descendants of a section are exactly the run of deeper sections that
//      follows it, so we walk forward until the depth drops back to the
//      clicked section's own level.

import { DocumentTypeSection } from "../../../types/documentTypes";

// Guards against a corrupt parent chain looping forever.
const MAX_ANCESTOR_WALK = 10;

/**
 * Index just past `index` and all of its descendants -- the position a new
 * sibling of `sections[index]` should be inserted at.
 */
export const getInsertIndexAfterSubtree = (
  sections: DocumentTypeSection[],
  index: number,
): number => {
  if (index < 0 || index >= sections.length) return sections.length;

  const parentById = new Map(
    sections.map((s) => [s.DocumentTypeSectionID, s.ParentSectionID]),
  );

  const depthOf = (section: DocumentTypeSection): number => {
    let depth = 0;
    let parentId: number | null | undefined = section.ParentSectionID;
    const visited = new Set<number>();
    while (parentId && !visited.has(parentId) && depth < MAX_ANCESTOR_WALK) {
      visited.add(parentId);
      depth++;
      parentId = parentById.get(parentId) ?? null;
    }
    return depth;
  };

  const baseDepth = depthOf(sections[index]);
  let cursor = index + 1;
  while (cursor < sections.length && depthOf(sections[cursor]) > baseDepth) {
    cursor++;
  }
  return cursor;
};
